import React, { useEffect, useState } from "react";
import { Box, CardContent, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import { getFormattedDate } from "utils/time.util";

const STYLE_LABELS = {
  apa: "APA",
  "modern-language-association": "MLA",
};

const OPTIONS = [
  { key: "notes", label: "Notes" },
  { key: "citation", label: "Citation" },
  { key: "readinglevel", label: "Reading Levels" },
];

/** One archived note, laid out like a note card in the live sidebar. */
function SnapshotNote({ note, theme }) {
  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="subtitle2"
        component="div"
        dangerouslySetInnerHTML={{ __html: note.noteText_chr || "" }}
      />
    </Box>
  );
}

/**
 * The bookmark sidebar for an archived submission. It mirrors the live sidebar
 * (features/Bookmarks/components/BookmarkDetails) but reads the frozen snapshot row
 * instead of the live source APIs — snapshot IDs are a different key space — and so
 * carries none of its editing, collection-picking, or saving controls.
 */
export default function SnapshotBookmarkSidebar({ source, option = "citation", onClose }) {
  const theme = useTheme();
  const [selectedOption, setSelectedOption] = useState(option);

  // Opening from the Notes or Citation cell lands on that view, as the live grid does.
  useEffect(() => setSelectedOption(option), [option, source]);

  if (!source) return null;

  const citation = source.citation || null;
  const notes = source.notes || [];

  const optionSx = (key) => ({
    fontStyle: "italic",
    cursor: "pointer",
    textDecoration: selectedOption === key ? "underline" : "none",
    textDecorationThickness: "2px",
    textUnderlineOffset: "8px",
    color: selectedOption === key ? theme.palette.primary.main : "#696b6c",
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ backgroundColor: theme.palette.secondary.main }}>
        <Stack direction="row" p={2} justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: "#696b6c", fontSize: "2rem", mb: 0 }}
          >
            Details
          </Typography>
          <AccessibleIconButton color="inherit" title="Close" edge="start" onClick={onClose}>
            <CloseIcon sx={{ color: "#a9baca", height: "2.2rem" }} />
          </AccessibleIconButton>
        </Stack>

        <Stack px={5} direction="column" gap={1}>
          <Tooltip title={source.title_chr || ""}>
            <Typography noWrap variant="body4">
              {source.title_chr || "-"}
            </Typography>
          </Tooltip>
          {source.sourceUrl_chr && (
            <Tooltip title={source.sourceUrl_chr}>
              <SearchResultLink
                url={source.sourceUrl_chr}
                trackingCat="click_resultset_title"
                trackingOptions={{
                  url: source.sourceUrl_chr,
                  entity: source.sourceName_chr,
                  type: "archived bookmark sidebar",
                  title: source.title_chr,
                  date_published: source.sourcePublishedDateTime_dtm,
                }}
                variant="body3"
                color="grey.600"
                noWrap
                sx={{
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                  cursor: "pointer",
                }}
              >
                {source.sourceUrl_chr}
              </SearchResultLink>
            </Tooltip>
          )}
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" color={theme.palette.grey[700]} fontWeight="bold">
              {source.sourceName_chr}
            </Typography>
            <Typography variant="body2" color="grey.600">
              {getFormattedDate(source.sourcePublishedDateTime_dtm)}
            </Typography>
          </Stack>
        </Stack>

        <Stack mt={2} mb="8px" direction="row" justifyContent="center" gap={3}>
          {OPTIONS.map((item) => (
            <Typography
              key={item.key}
              variant="h6"
              sx={optionSx(item.key)}
              onClick={() => setSelectedOption(item.key)}
            >
              {item.label}
            </Typography>
          ))}
        </Stack>
      </Box>

      <CardContent sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
        {selectedOption === "readinglevel" ? (
          <Box sx={{ p: 2 }}>
            {source.readingLevel_chr ? (
              <React.Fragment>
                <Typography sx={{ mb: 0.5 }} variant="body3" color="grey.600">
                  {source.readingLevelName_chr || "Reading Level"}
                </Typography>
                <Typography
                  variant="subtitle2"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: source.readingLevel_chr }}
                  sx={{ p: 1.5 }}
                />
              </React.Fragment>
            ) : (
              // Reading levels are generated on demand from the live article; a frozen
              // snapshot only shows one if the student saved it before archiving.
              <Typography color="grey.600">
                No reading level was saved for this source.
              </Typography>
            )}
          </Box>
        ) : selectedOption === "citation" ? (
          <Box sx={{ p: 2 }}>
            {citation ? (
              <React.Fragment>
                <Typography sx={{ mb: 0.5 }} variant="body3" color="grey.600">
                  {`Generated Citation${
                    STYLE_LABELS[citation.styles_chr] || citation.styles_chr
                      ? ` (${STYLE_LABELS[citation.styles_chr] || citation.styles_chr})`
                      : ""
                  }`}
                </Typography>
                <Typography
                  variant="subtitle2"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: citation.citation_chr || "" }}
                  sx={{ p: 1.5 }}
                />
              </React.Fragment>
            ) : (
              <Typography color="grey.600">
                No citation was saved for this source.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {notes.length > 0 ? (
              notes.map((note) => (
                <SnapshotNote key={note.archiveNoteID_ids} note={note} theme={theme} />
              ))
            ) : (
              <Typography color="grey.600">No notes were saved for this source.</Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Box>
  );
}
